/**
 * Gemeinsame Erfassungsquelle „Fremd-Tab" (web-tab UND ninox, Issue #61):
 * Die Anwendung läuft in einem eigenen Browser-Tab statt im iframe — der
 * Tester teilt mit ihr weder Origin noch Speicher. Der Screenshot kommt über
 * die Tab-Freigabe (getDisplayMedia, der Melder wählt den Anwendungs-Tab);
 * einmal verbunden, liefert ein Klick auf die Live-Vorschau jeden weiteren
 * Screenshot ohne Dialog. Das Bild geht an den Kern (`onBild`).
 *
 * Adapter-spezifisch sind nur Texte, die Öffnen-Buttons und ein optionaler
 * Hinweis-Block — siehe NinoxTabSource (Ninox-Login, Live/DEV) und
 * WebTabSource (SSO, Stage/Live).
 */

import { useEffect, useRef, useState, type ReactNode } from "react"
import { Camera, ExternalLink, MonitorUp, Unplug } from "lucide-react"
import { toast } from "sonner"
import type { AppZiel } from "@/components/AppSwitcher"
import { FremdTabCapture } from "@/lib/screenshot"
import type { Application } from "@/types"

export interface FremdTabTexte {
  /** Erklärung unter dem Anwendungsnamen */
  intro: string
  /** Kurzname des Tabs in Buttons/Badges, z. B. „Ninox-Tab" / „Anwendungs-Tab" */
  tab: string
  /** Beschriftung der Öffnen-Buttons je Ziel */
  oeffnen: { live: string; dev: string; einzig: string }
  /** Erläuterung unter den beiden Zielen (nur wenn `urlLive` gesetzt) */
  zieleHinweis: ReactNode
  /** Schritt 1 der Anleitung (Rest ist gemeinsam) */
  schritt1: ReactNode
  fehlerVerbinden: string
  fehlerVerloren: string
}

export interface FremdTabSourceProps {
  anwendung: Application
  /** null = noch kein Entwurf → „Melden"-Button; sonst Screenshot-Werkzeuge */
  entwurfId: number | null
  /** Entwurf beginnen (Kern-Mutation `beginnen`) */
  meldenStarten: () => void
  meldenLaeuft: boolean
  /** Aufgenommenes Bild — was der Kern damit macht, entscheidet die Linie */
  onBild: (bild: Blob) => void
  /** Fallback „Bild hochladen" — öffnet den Datei-Dialog des Kerns */
  onDateiWaehlen: () => void
  /** Anwendung im Fremd-Tab öffnen (openApp-Strategie des Adapters) */
  oeffnen: (ziel: AppZiel) => void
  texte: FremdTabTexte
  /** Optionaler Hinweis-Block unter den Öffnen-Buttons (z. B. Ninox-Login) */
  hinweis?: ReactNode
  /** Capture-Instanz des Adapters (Default: FremdTabCapture) */
  captureErzeugen?: () => FremdTabCapture
  /** Gesetzt = Melden gesperrt, mit diesem Hinweis (Bereitschafts-Gate) */
  meldenGesperrtGrund?: string
}

export function FremdTabSource({
  anwendung,
  entwurfId,
  meldenStarten,
  meldenLaeuft,
  onBild,
  onDateiWaehlen,
  oeffnen,
  texte,
  hinweis,
  captureErzeugen,
  meldenGesperrtGrund,
}: FremdTabSourceProps) {
  // Persistente Verbindung zum freigegebenen Tab (F4): einmal verbinden →
  // danach Sofort-Abgriff ohne Dialog/Tab-Wechsel.
  const captureRef = useRef<FremdTabCapture | null>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const [tabVerbunden, setTabVerbunden] = useState(false)
  const [tabVerbindet, setTabVerbindet] = useState(false)

  // Capture-Session lazy anlegen + beim Verlassen aufräumen
  if (captureRef.current === null) {
    captureRef.current = captureErzeugen ? captureErzeugen() : new FremdTabCapture()
  }
  useEffect(() => {
    const capture = captureRef.current!
    capture.onEnded = () => setTabVerbunden(false)
    return () => capture.trennen()
  }, [])

  // Live-Vorschau an das <video> hängen, sobald verbunden
  useEffect(() => {
    if (tabVerbunden && previewRef.current) {
      previewRef.current.srcObject = captureRef.current!.mediaStream
    }
  }, [tabVerbunden])

  const tabVerbinden = async () => {
    setTabVerbindet(true)
    try {
      await captureRef.current!.verbinden()
      setTabVerbunden(true)
    } catch (e) {
      // Abbruch im Freigabe-Dialog ist kein Fehler
      if ((e as Error).name !== "NotAllowedError") toast.error(texte.fehlerVerbinden)
    } finally {
      setTabVerbindet(false)
    }
  }

  const screenshotAufnehmen = async () => {
    if (!captureRef.current?.verbunden) {
      void tabVerbinden()
      return
    }
    try {
      onBild(await captureRef.current.frameAbgreifen())
    } catch {
      // Verbindung verloren (Tab geschlossen / Teilen beendet) → neu verbinden
      setTabVerbunden(false)
      toast.error(texte.fehlerVerloren)
    }
  }

  return (
    <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-muted px-4">
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="ml-2 truncate text-xs text-muted-foreground/80">
          {anwendung.url.replace(/^https?:\/\//, "")}
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-8 py-10">
        <div className="max-w-xl text-center">
          <h1 className="text-[22px] font-bold">{anwendung.name}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{texte.intro}</p>
        </div>
        {anwendung.urlLive ? (
          // Zwei Ziele — Live (echte Daten) und DEV/Stage (Testdaten)
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => oeffnen("live")}
                className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
              >
                <ExternalLink className="h-4 w-4" />
                {texte.oeffnen.live}
              </button>
              <button
                type="button"
                onClick={() => oeffnen("dev")}
                className="flex items-center gap-2 rounded-xl bg-secondary px-6 py-3 text-[15px] font-bold hover:bg-secondary/80"
              >
                <ExternalLink className="h-4 w-4" />
                {texte.oeffnen.dev}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{texte.zieleHinweis}</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => oeffnen("dev")}
            className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
          >
            <ExternalLink className="h-4 w-4" />
            {texte.oeffnen.einzig}
          </button>
        )}
        {hinweis}
        <ol className="max-w-xl space-y-2.5 rounded-[10px] border bg-muted/30 px-5 py-4 text-[13px] text-muted-foreground">
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">1.</span>
            <span>{texte.schritt1}</span>
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">2.</span>
            <span>
              <span className="font-semibold text-foreground">„Melden“</span> starten und den{" "}
              {texte.tab} <span className="font-semibold text-foreground">einmal verbinden</span>{" "}
              (der Browser fragt, welcher Tab geteilt wird — den Tab der Anwendung wählen).
            </span>
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">3.</span>
            <span>
              Danach genügt ein Klick auf die{" "}
              <span className="font-semibold text-foreground">Live-Vorschau</span> für jeden
              Screenshot — ohne erneuten Dialog, ohne Tab-Wechsel. Markieren, beschreiben,
              absenden.
            </span>
          </li>
        </ol>

        {/* Live-Vorschau des verbundenen Tabs = Auslöser (F4) */}
        {entwurfId !== null && tabVerbunden && (
          <div className="w-full max-w-xl">
            <button
              type="button"
              onClick={() => void screenshotAufnehmen()}
              title={`Klicken für einen Screenshot (${texte.tab})`}
              className="group relative block w-full overflow-hidden rounded-xl border-2 border-primary/30 bg-black shadow-card transition-colors hover:border-primary"
            >
              <video
                ref={previewRef}
                autoPlay
                muted
                playsInline
                className="aspect-video w-full object-contain"
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/70 to-transparent py-3 text-[13px] font-bold text-white">
                <Camera className="h-4 w-4" /> Screenshot aufnehmen
              </span>
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {texte.tab}{" "}
                live
              </span>
            </button>
            <div className="mt-1.5 flex items-center justify-between text-[12px] text-muted-foreground">
              <span>Verbunden — Vorschau anklicken für jeden weiteren Screenshot.</span>
              <button
                type="button"
                onClick={() => {
                  captureRef.current?.trennen()
                  setTabVerbunden(false)
                }}
                className="flex items-center gap-1 font-semibold hover:text-foreground"
              >
                <Unplug className="h-3 w-3" /> Verbindung trennen
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Schwebende Melden-Leiste (§8) */}
      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2.5 rounded-full border bg-card px-3.5 py-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
        <span className="whitespace-nowrap pl-1 text-[13px] font-semibold">{anwendung.name}</span>
        {entwurfId === null ? (
          <button
            type="button"
            onClick={meldenStarten}
            disabled={meldenLaeuft || !!meldenGesperrtGrund}
            title={meldenGesperrtGrund}
            className="rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {meldenGesperrtGrund ? "Wird vorbereitet …" : "Melden"}
          </button>
        ) : (
          <>
            {tabVerbunden ? (
              <button
                type="button"
                onClick={() => void screenshotAufnehmen()}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover"
              >
                <Camera className="h-[15px] w-[15px]" />
                Screenshot
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void tabVerbinden()}
                disabled={tabVerbindet}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                <MonitorUp className="h-[15px] w-[15px]" />
                {tabVerbindet ? "Verbinde …" : `${texte.tab} verbinden`}
              </button>
            )}
            <button
              type="button"
              onClick={onDateiWaehlen}
              className="whitespace-nowrap rounded-full bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
              title="Fallback: Bilddatei hochladen"
            >
              Bild hochladen
            </button>
          </>
        )}
      </div>
    </div>
  )
}
